// Ported from dev-scrips-pm-react src/documents/services/use-patient-documents.ts (PROD-1119 documents module).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  GetPatientDocumentsRequest,
  PatientDocumentModel,
} from '@/documents/types/document';

/**
 * POST /api/Patients/GetPatientDocuments
 *
 * Mirrors Flutter `DocumentDataSourceImpl.getPatientDocuments`
 * (`document_data_source_impl.dart:26-41`). Wire body dev-curl verified
 * 2026-05-14 (Andrew shared a working request with `encounterId: null`
 * — confirms the backend accepts the no-encounter case and returns the
 * patient's documents unfiltered by encounter).
 *
 * Although the verb is POST, this is a read-style endpoint (no side
 * effects). Wired as `useQuery` so rapid filter toggles serve from
 * cache. AbortSignal forwarded per the project rule (every queryFn
 * destructures `{ signal }` and passes through).
 *
 * Cache key includes every filter param — the Phase 2 filter widget
 * surfaces all of them (PROD-XXX documents-filter-and-cleanup Phase 2).
 * Flipping any filter refetches; preserving identical params serves
 * from cache.
 *
 * `enabled` is gated on `patientId` being non-empty. The hook does
 * NOT require `encounterId` — both consumers (panel Docs tab + future
 * patient-chart Docs tab) want the patient-scoped list when no
 * encounter is selected.
 *
 * Pagination (Samer review PR #134 §2):
 *   `pageSize` defaults to 20 (matches Flutter). The hook exposes
 *   `pageNumber` + `pageSize` on its params so a caller CAN paginate,
 *   but the current Docs tab consumer doesn't surface pager UI — V1
 *   ships a single page of 20. A patient with >20 documents silently
 *   truncates. This is an explicit MVP limit tracked in PROD-XXX
 *   documents-filter-and-cleanup Phase 2 (filter widget + pagination
 *   UI land together — the filter widget itself needs paging to be
 *   useful, so they're co-scoped).
 */

export interface UsePatientDocumentsParams {
  patientId: string | null | undefined;
  encounterId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  fileType?: string[];
  uploadedBy?: string[];
  pageNumber?: number;
  pageSize?: number;
  isArchived?: boolean;
  /** Set to false to skip the request entirely. Defaults to true. */
  enabled?: boolean;
}

export function usePatientDocuments(
  params: UsePatientDocumentsParams,
): UseQueryResult<PatientDocumentModel[]> {
  const {
    patientId,
    encounterId,
    startDate,
    endDate,
    fileType,
    uploadedBy,
    pageNumber = 1,
    pageSize = 20,
    isArchived = false,
    enabled = true,
  } = params;

  return useQuery<PatientDocumentModel[]>({
    queryKey: [
      'patient-documents',
      patientId,
      encounterId ?? null,
      startDate ?? null,
      endDate ?? null,
      fileType ?? [],
      uploadedBy ?? [],
      pageNumber,
      pageSize,
      isArchived,
    ],
    enabled: enabled && !!patientId,
    queryFn: ({ signal }) => {
      const body: GetPatientDocumentsRequest = {
        patientId: patientId as string,
        encounterId: encounterId ?? null,
        startDate: startDate ?? null,
        endDate: endDate ?? null,
        fileType: (fileType ?? []) as never,
        uploadedBy: uploadedBy ?? [],
        pageNumber,
        pageSize,
        isArchived,
      };
      return post<PatientDocumentModel[]>(
        '/api/Patients/GetPatientDocuments',
        body,
        { signal },
      );
    },
  });
}
